import React, { useEffect, useMemo, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "../../api/api";

import { SCAI_MANAGEMENTS } from "../master/scaiManagementConfig";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import StaffLayout from "./StaffLayout";

import "./StaffAccessManagement.css";



// URL: /staff/access?staff_id=123. Route lives inside your existing SideBar/Nav layout.

// The backend must independently authorize every request; hiding a button is not security.

const ROOT = "/staff/access";

const DASHBOARD = "/";

const norm = (p) => String(p || "").split(/[?#]/)[0].replace(/\/+$/, "") || "/";

const unwrap = (response) => response?.data?.data ?? response?.data ?? response;

const allModules = SCAI_MANAGEMENTS.filter((m) => m.key !== "dashboard_management")

  .map((m) => {

    const paths = new Map();

    (m.routes || []).forEach((r) => {

      if (r.path && norm(r.path) !== "/") paths.set(norm(r.path), {

        path: norm(r.path), title: r.title || r.path, action: r.action || "view", tabId: null,

      });

    });

    (m.tabs || []).forEach((t) => {

      const all = [t.path, ...(t.activePaths || []), ...(t.subItems || []).map((s) => s.path)];

      all.forEach((p) => { if (!p || norm(p) === "/") return;

        const item = paths.get(norm(p));

        if (item) paths.set(norm(p), { ...item, tabId: item.tabId || t.id,

          title: item.title === item.path ? (t.title || t.label || p) : item.title });

        // Don't create an access grant for paths not present in the route catalog.

      });

    });

    return { ...m, catalogRoutes: Array.from(paths.values()), tabs: m.tabs || [] };

  });

const totalRoutes = allModules.reduce((n, m) => n + m.catalogRoutes.length, 0);

const statusMessage = (err) => err?.response?.data?.message || err?.message || "Request failed";



export default function StaffAccessManagement() {

  const navigate = useNavigate();

  const [params, setParams] = useSearchParams();

  const staffId = params.get("staff_id") || "";

  const [staffSearch, setStaffSearch] = useState("");

  const [staffs, setStaffs] = useState([]);

  const [staffLoading, setStaffLoading] = useState(false);

  const [activeKey, setActiveKey] = useState(allModules[0]?.key || "");

  const [routeSearch, setRouteSearch] = useState("");

  const [selected, setSelected] = useState(new Set());

  const [baseline, setBaseline] = useState(new Set());

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");

  const [error, setError] = useState("");

  const active = allModules.find((m) => m.key === activeKey) || allModules[0];

  const selectedStaff = staffs.find((s) => String(s.id) === String(staffId));

  const changed = useMemo(() => selected.size !== baseline.size || [...selected].some((p) => !baseline.has(p)), [selected, baseline]);

  const enabledCount = selected.size;

  const visibleRoutes = useMemo(() => (active?.catalogRoutes || []).filter((r) =>

    [r.path, r.title, r.action, active?.tabs.find((t) => t.id === r.tabId)?.title || ""]

      .some((s) => String(s).toLowerCase().includes(routeSearch.toLowerCase()))

  ), [active, routeSearch]);

  const tabsForActive = useMemo(() => {

    if (!active) return [];

    return active.tabs.map((t) => ({

      ...t, children: active.catalogRoutes.filter((r) => r.tabId === t.id),

    })).filter((t) => t.children.length);

  }, [active]);



  useEffect(() => {

    let live = true;

    const timer = setTimeout(async () => {

      setStaffLoading(true);

      try {

        const result = unwrap(await api.get(`${ROOT}/staff-search`, { params: { search: staffSearch } }));

        if (live) setStaffs(Array.isArray(result.staffs) ? result.staffs : []);

      } catch (e) { if (live) setError(statusMessage(e)); }

      finally { if (live) setStaffLoading(false); }

    }, 250);

    return () => { live = false; clearTimeout(timer); };

  }, [staffSearch]);

  useEffect(() => {

    if (!staffId) { setSelected(new Set()); setBaseline(new Set()); return; }

    let live = true;

    setLoading(true); setError(""); setMessage("");

    api.get(`${ROOT}/${encodeURIComponent(staffId)}`).then((response) => {

      if (!live) return;

      const data = unwrap(response);

      const paths = new Set((data.paths || []).map(norm));

      setSelected(paths); setBaseline(new Set(paths));

      if (data.staff) setStaffs((old) => old.some((s) => String(s.id) === String(data.staff.id))

        ? old : [data.staff, ...old]);

    }).catch((e) => { if (live) setError(statusMessage(e)); })

      .finally(() => { if (live) setLoading(false); });

    return () => { live = false; };

  }, [staffId]);



  function togglePaths(paths, checked) {

    setSelected((old) => {

      const next = new Set(old);

      paths.forEach((p) => { if (checked) next.add(p); else next.delete(p); });

      return next;

    });

  }

  function changeStaff(id) { if (changed && !window.confirm("Discard unsaved permission changes?")) return;

    if (id) setParams({ staff_id: String(id) }); else setParams({}); }

  function checkAllState(paths) {

    const count = paths.filter((p) => selected.has(p)).length;

    return count === 0 ? "none" : count === paths.length ? "all" : "some";

  }

  async function save() {

    if (!staffId || saving) return;

    if (!window.confirm(`Replace this staff member's direct access with ${selected.size} selected routes?`)) return;

    setSaving(true); setError(""); setMessage("");

    try {

      const response = unwrap(await api.post(`${ROOT}/${encodeURIComponent(staffId)}`, {

        paths: [...selected].sort(),

      }));

      const paths = new Set((response.paths || []).map(norm));

      setSelected(paths); setBaseline(new Set(paths));

      setMessage("Access permissions saved successfully. Staff should sign in again to refresh their session.");

    } catch (e) { setError(statusMessage(e)); }

    finally { setSaving(false); }

  }

  function applyPreset(kind) {

    if (!active) return;

    const paths = active.catalogRoutes.filter((r) => kind === "all" ||

      (kind === "read" && ["view", "report", "print"].includes(r.action)))

      .map((r) => r.path);

    togglePaths(paths, true);

  }

  return (
    <div className="layout-wrapper layout-content-navbar scai-staff-access-page">
      <div className="layout-container">
        <Header />
        <div className="layout-page">
          <Nav />
          <div className="content-wrapper">
            <div className="container-fluid mt-2">
              <StaffLayout />
              <main className="sa-root">

      <header className="sa-header">

        <div><div className="sa-eyebrow">SCAI · STAFF SECURITY</div><h1><i className="ri-shield-user-line"/> Staff Access Management</h1>

          <p>Grant management, tab and individual route access. Dashboard is available to every logged-in staff member.</p></div>

        <div className="sa-header-actions"><span className="sa-stat">{allModules.length} modules · {totalRoutes} routes</span>

          <button className="sa-btn" onClick={() => navigate("/staffs")}>Back to Staff</button></div>

      </header>

      <section className="sa-toolbar">

        <label className="sa-field"><span>Select staff member</span>

          <input value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} placeholder="Search by name, mobile or registration..." />

          <select value={staffId} onChange={(e) => changeStaff(e.target.value)}>

            <option value="">{staffLoading ? "Loading staff..." : "Select staff"}</option>

            {staffId && !selectedStaff && <option value={staffId}>Staff #{staffId}</option>}

            {staffs.map((staff) => <option key={staff.id} value={staff.id}>{staff.name} · {staff.registration_no || staff.mobile || `#${staff.id}`}</option>)}

          </select>

        </label>

        <div className="sa-toolbar-summary"><div><strong>{enabledCount}</strong><span>Direct routes selected</span></div>

          <div><strong>{allModules.filter((m) => m.catalogRoutes.some((r) => selected.has(r.path))).length}</strong><span>Modules selected</span></div></div>

        <div className="sa-actions"><button className="sa-btn" disabled={!changed || saving} onClick={() => setSelected(new Set(baseline))}>Reset</button>

          <button className="sa-btn sa-primary" disabled={!staffId || loading || saving || !changed} onClick={save}>{saving ? "Saving..." : "Save access"}</button></div>

      </section>

      {error && <div role="alert" className="sa-alert sa-error">{error}</div>}

      {message && <div role="status" className="sa-alert sa-success">{message}</div>}

      <div className="sa-layout">

        <aside className="sa-modules"><div className="sa-panel-title">Managements</div>

          {allModules.map((m) => {

            const count = m.catalogRoutes.filter((r) => selected.has(r.path)).length;

            return <button key={m.key} className={`sa-module ${activeKey === m.key ? "is-active" : ""}`} onClick={() => {setActiveKey(m.key);setRouteSearch("");}}>

              <i className={m.icon || "ri-apps-line"}/><span><strong>{m.label || m.title}</strong><small>{count} / {m.catalogRoutes.length} enabled</small></span>

              {count > 0 && <b>{count}</b>}</button>;

          })}

        </aside>

        <section className="sa-details">

          {!staffId ? <div className="sa-empty"><i className="ri-user-settings-line"/><h3>Select a staff member</h3>

            <p>Choose a staff member to load and modify their direct route permissions.</p></div> : loading ?

            <div className="sa-empty">Loading current access...</div> : <>

            <div className="sa-details-head"><div><h2><i className={active.icon}/> {active.title}</h2>

              <p>{active.subtitle}</p></div><div className="sa-details-actions">

              <button className="sa-btn" onClick={() => applyPreset("read")}>Grant read-only</button>

              <button className="sa-btn" onClick={() => applyPreset("all")}>Grant all</button>

              <button className="sa-btn" onClick={() => togglePaths(active.catalogRoutes.map((r) => r.path), false)}>Clear module</button></div></div>

            <div className="sa-module-select"><label><input type="checkbox"

              ref={(el) => { if(el) el.indeterminate = checkAllState(active.catalogRoutes.map((r) => r.path)) === "some"; }}

              checked={checkAllState(active.catalogRoutes.map((r) => r.path)) === "all"}

              onChange={(e) => togglePaths(active.catalogRoutes.map((r) => r.path), e.target.checked)}/>

              Enable all routes in {active.label || active.title}</label>

              <span>{active.catalogRoutes.filter((r) => selected.has(r.path)).length} of {active.catalogRoutes.length}</span></div>

            <div className="sa-tabs"><span>Tabs & routes</span><input value={routeSearch} onChange={(e)=>setRouteSearch(e.target.value)} placeholder="Filter routes..." /></div>

            {tabsForActive.map((tab) => {

              const displayed = tab.children.filter((r) => visibleRoutes.includes(r));

              if (!displayed.length) return null;

              const tabPaths = tab.children.map((r) => r.path);

              return <div className="sa-tab-block" key={tab.id}>

                <div className="sa-tab-heading"><label><input type="checkbox"

                  ref={(el)=>{if(el) el.indeterminate = checkAllState(tabPaths) === "some";}}

                  checked={checkAllState(tabPaths) === "all"}

                  onChange={(e)=>togglePaths(tabPaths,e.target.checked)}/>

                  <i className={tab.icon || "ri-file-list-line"}/><strong>{tab.title || tab.label}</strong></label>

                  <span>{tabPaths.filter((p)=>selected.has(p)).length}/{tabPaths.length}</span></div>

                <div className="sa-route-grid">{displayed.map((r) => <label className="sa-route" key={r.path}>

                  <input type="checkbox" checked={selected.has(r.path)} onChange={(e)=>togglePaths([r.path],e.target.checked)}/>

                  <span className="sa-route-body"><strong>{r.title}</strong><code>{r.path}</code></span>

                  <small className={`sa-action sa-${r.action}`}>{r.action}</small></label>)}</div>

              </div>;

            })}

            {visibleRoutes.filter((r) => !r.tabId).length > 0 && <div className="sa-tab-block">

              <div className="sa-tab-heading"><strong><i className="ri-links-line"/> Additional routes</strong></div>

              <div className="sa-route-grid">{visibleRoutes.filter((r)=>!r.tabId).map((r)=><label className="sa-route" key={r.path}>

                <input type="checkbox" checked={selected.has(r.path)} onChange={(e)=>togglePaths([r.path],e.target.checked)}/>

                <span className="sa-route-body"><strong>{r.title}</strong><code>{r.path}</code></span><small className="sa-action">{r.action}</small>

              </label>)}</div></div>}

            <div className="sa-bottom-actions"><span>{changed ? "Unsaved changes" : "All changes saved"}</span>

              <button className="sa-btn sa-primary" disabled={!changed || saving} onClick={save}>Save permissions</button></div>

          </>}

        </section>

      </div>

              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

}
